import React, { Component } from "react";
import { createPortal } from "react-dom";
import { I18n } from "react-redux-i18n";
import { hasRole } from "../../js/admin";
import { subscribe, startSession, stopSession, submitDraft, setLineCount, setLineComment, removeLine, clearDraft, getTableInfo, confirmPending, dismissPending, setPanelOpen } from "../../js/ai/voiceOrder";

const t = (key, defaultValue) => I18n.t("admin_local." + key, { defaultValue });

const errorText = code => {
  switch (code) {
    case "not_configured":
      return t("voice_order_not_configured", "Voice ordering is not set up on the server.");
    case "session_failed":
      return t("voice_order_session_failed", "Could not start the voice assistant. Try again in a moment.");
    case "mic":
      return t("voice_order_mic", "The microphone is not available. Allow microphone access for this app.");
    case "connection":
      return t("voice_order_connection", "The voice connection was lost.");
    case "no_table":
      return t("voice_order_no_table", "Select a table first, then add the list to its order.");
    case "lock_failed":
      return t("voice_order_lock_failed", "The table could not be opened. Open it on the screen first.");
    case "order_incomplete":
      return t("voice_order_incomplete", "Not everything was ordered. The rest stays on the list.");
    case "print_failed":
      return t("voice_order_print_failed", "The items were added but could not be submitted and printed. Use Order and print.");
    default:
      return "";
  }
};

const activityText = (status, activity) => {
  if (status === "connecting") return t("voice_order_connecting", "Connecting...");
  if (status !== "active") return t("voice_order_stopped", "Microphone is off");
  switch (activity) {
    case "thinking":
      return t("voice_order_thinking", "Thinking...");
    case "speaking":
      return t("voice_order_speaking", "Speaking...");
    default:
      return t("voice_order_listening", "Listening, say what the guests want");
  }
};

const costText = cost => {
  const money = "$" + cost.total.toFixed(cost.total < 0.1 ? 3 : 2);
  const cached = cost.inputTokens > 0 ? Math.round((100 * cost.cachedTokens) / cost.inputTokens) : 0;
  return (cost.ended ? t("voice_order_cost_total", "Session cost") : t("voice_order_cost_so_far", "Cost so far")) + ": ~" + money + " (" + cost.seconds + " s, " + cached + "% cached)";
};

const iconStyle = { paddingLeft: "4px", paddingRight: "4px", cursor: "pointer", display: "flex", alignItems: "center", color: "rgb(131, 123, 108)", position: "relative" };
const panelStyle = {
  position: "fixed",
  top: "56px",
  right: "12px",
  width: "360px",
  maxWidth: "calc(100vw - 24px)",
  maxHeight: "calc(100vh - 80px)",
  display: "flex",
  flexDirection: "column",
  background: "#fff",
  color: "#333",
  border: "1px solid #ccc",
  borderRadius: "8px",
  boxShadow: "0 4px 18px rgba(0,0,0,0.3)",
  zIndex: 100000,
  fontSize: "14px"
};
const smallButton = { border: "1px solid #bbb", background: "#f5f5f5", borderRadius: "4px", minWidth: "30px", height: "30px", cursor: "pointer", color: "#333" };

class VoiceOrderButton extends Component {
  state = { voice: { status: "idle", activity: "", error: null, lines: [], messages: [], submitting: false, pending: [], notice: null, panelOpen: false }, tick: 0 };

  componentDidMount() {
    this.unsubscribe = subscribe(voice => this.setState({ voice }));
    // the selected table is kept in sessionStorage by the rest of the app, so look at it again now and then
    this.timer = setInterval(() => this.state.voice.panelOpen && this.setState({ tick: this.state.tick + 1 }), 1000);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.voice.panelOpen && prevState.voice.messages.length !== this.state.voice.messages.length && this.log) {
      this.log.scrollTop = this.log.scrollHeight;
    }
  }

  componentWillUnmount() {
    this.unsubscribe && this.unsubscribe();
    clearInterval(this.timer);
  }

  open = () => {
    setPanelOpen(true);
    startSession();
  };

  close = () => {
    stopSession();
    setPanelOpen(false);
  };

  toggleMic = () => {
    if (this.state.voice.status === "idle") startSession();
    else stopSession();
  };

  submit = () => {
    submitDraft().then(left => {
      if (left === 0) this.close();
    });
  };

  renderPanel() {
    const { voice } = this.state;
    const { hasTable, tableName } = getTableInfo();
    const micOn = voice.status !== "idle";
    const total = voice.lines.reduce((sum, l) => sum + l.count, 0);
    const error = errorText(voice.error);
    return (
      <div style={panelStyle} role="dialog" aria-label={t("voice_order_title", "Voice order")}>
        <div style={{ display: "flex", alignItems: "center", padding: "8px 10px", borderBottom: "1px solid #ddd", gap: "8px" }}>
          <strong style={{ flex: 1 }}>
            {t("voice_order_title", "Voice order")}
            {hasTable ? " - " + tableName : ""}
          </strong>
          <button type="button" style={smallButton} onClick={this.close} title={t("voice_order_close", "Close")} aria-label={t("voice_order_close", "Close")}>
            &times;
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", padding: "8px 10px", gap: "10px" }}>
          <button
            type="button"
            onClick={this.toggleMic}
            title={micOn ? t("voice_order_stop", "Turn the microphone off") : t("voice_order_start", "Turn the microphone on")}
            style={{ ...smallButton, width: "40px", height: "40px", borderRadius: "20px", background: micOn ? "#d9534f" : "#f5f5f5", color: micOn ? "#fff" : "#333" }}
          >
            {this.micSvg()}
          </button>
          <span style={{ flex: 1 }}>{activityText(voice.status, voice.activity)}</span>
        </div>

        {error && <div style={{ padding: "6px 10px", background: "#fdecea", color: "#a94442" }}>{error}</div>}

        {voice.cost && <div style={{ padding: "4px 10px", background: "#f3f6fb", color: "#456", fontSize: "12px" }}>{costText(voice.cost)}</div>}

        {voice.notice && (
          <div style={{ padding: "6px 10px", background: voice.notice.ok ? "#e8f5e9" : "#fdecea", color: voice.notice.ok ? "#2e7d32" : "#a94442" }}>
            {(voice.notice.ok ? t("voice_action_done", "Done") : t("voice_action_failed", "It did not work")) + ": " + voice.notice.text}
          </div>
        )}

        {voice.pending.map(p => (
          <div key={p.id} style={{ padding: "8px 10px", background: "#fff8e1", borderTop: "1px solid #eee" }}>
            <div style={{ marginBottom: "6px", fontWeight: "bold" }}>{p.text}</div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button type="button" onClick={() => confirmPending(p.id)} style={{ flex: 1, height: "34px", border: "none", borderRadius: "4px", background: "#5cb85c", color: "#fff", fontWeight: "bold", cursor: "pointer" }}>
                {t("voice_action_confirm", "Confirm")}
              </button>
              <button type="button" onClick={() => dismissPending(p.id)} style={{ ...smallButton, padding: "0 10px", height: "34px" }}>
                {t("voice_action_dismiss", "Dismiss")}
              </button>
            </div>
          </div>
        ))}

        {voice.messages.length > 0 && (
          <div ref={el => (this.log = el)} style={{ overflowY: "auto", maxHeight: "130px", padding: "4px 10px", borderTop: "1px solid #eee", background: "#fafafa" }}>
            {voice.messages.map((m, i) => (
              <div key={i} style={{ margin: "2px 0", textAlign: m.role === "waiter" ? "right" : "left", color: m.role === "waiter" ? "#333" : "#666" }}>
                {m.text}
              </div>
            ))}
          </div>
        )}

        <div style={{ overflowY: "auto", flex: 1, borderTop: "1px solid #ddd", minHeight: "60px" }}>
          {voice.lines.length === 0 && <div style={{ padding: "10px", color: "#888" }}>{t("voice_order_empty", "The list is empty.")}</div>}
          {voice.lines.map(line => (
            <div key={line.id} style={{ padding: "6px 10px", borderBottom: "1px solid #eee" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <button type="button" style={smallButton} onClick={() => setLineCount(line.id, line.count - 1)} aria-label="-">
                  -
                </button>
                <span style={{ minWidth: "22px", textAlign: "center", fontWeight: "bold" }}>{line.count}</span>
                <button type="button" style={smallButton} onClick={() => setLineCount(line.id, line.count + 1)} aria-label="+">
                  +
                </button>
                <span style={{ flex: 1, paddingLeft: "4px" }}>
                  {line.name}
                  {line.size ? " (" + line.size + ")" : ""}
                  {line.details ? <div style={{ fontSize: "12px", color: "#777" }}>{line.details}</div> : null}
                </span>
                <button type="button" style={smallButton} onClick={() => removeLine(line.id)} title={t("voice_order_remove", "Remove")} aria-label={t("voice_order_remove", "Remove")}>
                  &times;
                </button>
              </div>
              <input
                type="text"
                value={line.comment || ""}
                placeholder={t("voice_order_comment", "Comment")}
                onChange={e => setLineComment(line.id, e.target.value)}
                style={{ width: "100%", marginTop: "4px", boxSizing: "border-box", padding: "3px 6px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
            </div>
          ))}
        </div>

        <div style={{ padding: "8px 10px", borderTop: "1px solid #ddd", display: "flex", gap: "8px", alignItems: "center" }}>
          <button type="button" style={{ ...smallButton, padding: "0 10px" }} disabled={voice.submitting || (voice.lines.length === 0 && voice.messages.length === 0)} onClick={clearDraft}>
            {t("voice_order_clear", "Clear")}
          </button>
          <button
            type="button"
            disabled={voice.submitting || total === 0 || !hasTable}
            onClick={this.submit}
            title={hasTable ? "" : errorText("no_table")}
            style={{ flex: 1, height: "36px", border: "none", borderRadius: "4px", background: voice.submitting || total === 0 || !hasTable ? "#bbb" : "#5cb85c", color: "#fff", fontWeight: "bold", cursor: "pointer" }}
          >
            {t("voice_order_add", "Add to table order")}
            {total > 0 ? " (" + total + ")" : ""}
          </button>
        </div>
      </div>
    );
  }

  micSvg() {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="3" width="6" height="11" rx="3" />
        <path d="M5 11a7 7 0 0 0 14 0" />
        <path d="M12 18v3" />
      </svg>
    );
  }

  render() {
    // the audio chat is only for superadmins for now
    if (!hasRole(["superadmin"])) return null;
    const { voice } = this.state;
    const open = voice.panelOpen;
    const title = t("voice_order_open", "Order by voice") + (voice.cost ? " - " + costText(voice.cost) : "");
    const pending = voice.lines.reduce((sum, l) => sum + l.count, 0);
    return (
      <>
        <div
          className="hide-print voice-order-button-header"
          role="button"
          tabIndex={0}
          title={title}
          aria-label={title}
          onClick={open ? this.close : this.open}
          onKeyDown={e => (e.key === "Enter" || e.key === " ") && (open ? this.close() : this.open())}
          style={{ ...iconStyle, color: voice.status === "active" ? "#d9534f" : iconStyle.color }}
        >
          {this.micSvg()}
          {!open && pending > 0 && (
            <span style={{ position: "absolute", top: "-4px", right: "-4px", minWidth: "16px", height: "16px", borderRadius: "8px", background: "#d9534f", color: "#fff", fontSize: "11px", lineHeight: "16px", textAlign: "center" }}>{pending}</span>
          )}
        </div>
        {open && this.props.withPanel !== false && createPortal(this.renderPanel(), document.body)}
      </>
    );
  }
}

export default VoiceOrderButton;
